/**
 * Sprint 63 — Porciones: capturar un ingrediente y venderlo en varios tamaños.
 *
 * El negocio vende la misma carne en cuatro precios:
 *
 *   Picaña 1 kg · Picaña 3/4 kg · Picaña 1/2 kg · Picaña 1/4 kg
 *
 * Hoy eso son **cinco capturas** —el ingrediente más cuatro productos, cada uno con su
 * cantidad y su vínculo— y cinco lugares donde equivocarse. La queja del dueño fue
 * exactamente ésa: *"todo quedó desconectado y MUY complejo de administrar"*.
 *
 * Aquí vive la parte pura: proponer las porciones habituales, nombrarlas como las
 * nombra el negocio, y **validar que el nombre concuerde con la cantidad**.
 *
 * Esa última validación no es cosmética. En la lista que mandó el negocio venía
 * *"Picaña 3/4 kg (500 gramos)"* — tres cuartos de kilo son 750 g, no 500. Un dedazo
 * ahí descuenta un tercio de menos en cada venta, para siempre, y no hay pantalla donde
 * se note. El sistema tiene que atraparlo al capturar.
 */

import { decirCantidad, esUnidadContinua } from './medidas';
import { describirFraccion } from './fraccionInsumo';

/** Una porción a crear: cómo se llama, cuánto lleva y a cuánto se vende. */
export interface Porcion {
  /** Sufijo del nombre del producto: "1 kg", "1/2 kg", "Entero", "Medio". */
  etiqueta: string;
  /** Cantidad del ingrediente que consume, en su unidad. */
  cantidad: number;
  /** Precio de venta en centavos. */
  precio: number;
}

/**
 * Fracciones que un restaurante **nombra** de verdad.
 *
 * Sprint 127 — Salió `1/8`. No porque no se venda —de ahí nacieron los 125 g de costilla que
 * originaron este sprint— sino porque nadie pide "un octavo de kilo": pide *125 gramos*. Se
 * sigue **leyendo** cualquier fracción que el negocio teclee a mano (`cantidadDeEtiqueta`); lo
 * que cambia es lo que el sistema propone solo.
 */
const NOMBRES_FRACCION: Array<{ valor: number; nombre: string }> = [
  { valor: 1, nombre: '1' },
  { valor: 0.75, nombre: '3/4' },
  { valor: 0.5, nombre: '1/2' },
  { valor: 0.25, nombre: '1/4' },
];

const EPSILON = 1e-6;

/**
 * Cómo se llama una porción en el menú.
 *
 * En peso y volumen se usa la fracción ("1/2 kg") cuando existe, porque así lo pide el
 * cliente en la mesa, y **gramos o mililitros** cuando no ("125 g"): es como se dice un
 * corte chico en cualquier carnicería. En piezas se dice "Entero" y "Medio": nadie pide
 * "0.5 pollos".
 */
export function etiquetaDePorcion(cantidad: number, unidad: string): string {
  const u = unidad.trim().toLowerCase();

  if (!esUnidadContinua(u)) {
    if (Math.abs(cantidad - 1) < EPSILON) return 'Entero';
    if (Math.abs(cantidad - 0.5) < EPSILON) return 'Medio';
    if (Math.abs(cantidad - 0.25) < EPSILON) return 'Un cuarto';
    return `${limpiar(cantidad)} ${unidad}`;
  }

  const frac = NOMBRES_FRACCION.find((f) => Math.abs(f.valor - cantidad) < EPSILON);
  if (frac) return `${frac.nombre} ${unidad}`;
  return decirCantidad(cantidad, u);
}

/**
 * De qué magnitud habla el sufijo de una etiqueta, y en qué escala.
 *
 * `sub` = la unidad chica de esa magnitud (gramo, mililitro): vale una milésima de la base.
 */
const SUFIJOS: Array<{ patron: RegExp; magnitud: 'peso' | 'volumen'; sub: boolean }> = [
  { patron: /\b(kgs?|kilos?|kilogramos?)\b\.?$/, magnitud: 'peso', sub: false },
  { patron: /\b(g|gr|grs|gramos?)\b\.?$/, magnitud: 'peso', sub: true },
  { patron: /\b(lts?|litros?|l)\b\.?$/, magnitud: 'volumen', sub: false },
  { patron: /\b(ml|mls|mililitros?|cc)\b\.?$/, magnitud: 'volumen', sub: true },
];

/** La magnitud de la unidad en que se mide el ingrediente. */
function magnitudDeUnidad(unidad: string): 'peso' | 'volumen' | 'conteo' {
  const u = unidad.trim().toLowerCase();
  if (u === 'kg') return 'peso';
  if (u === 'litro') return 'volumen';
  return 'conteo';
}

/**
 * Lo que la etiqueta *dice* que pesa, si se puede deducir. `null` si no se puede.
 *
 * Sirve para el único propósito de contrastarlo con la cantidad capturada: el sistema
 * no adivina el peso, sólo detecta que el nombre y el número no concuerdan. Es la red que
 * cazó *"Picaña 3/4 kg (500 gramos)"* en la lista que mandó el negocio — un dedazo que
 * descuenta un tercio de menos en cada venta, para siempre, sin pantalla donde se note.
 *
 * ---
 *
 * Sprint 127 — Ahora lee **gramos y mililitros**, y aprende a callarse.
 *
 * Al abrir la captura en gramos, un renglón llamado "125 g" con cantidad `0.125` habría
 * disparado el aviso *"dice 125 pero lleva 0.125"* en cada tamaño chico del catálogo. Un aviso
 * que sale siempre se ignora siempre — y el día que aparezca el dedazo de verdad, también.
 *
 * Las dos reglas nuevas, y las dos son para no opinar de más:
 * - **Magnitud incompatible = silencio.** "Coca Cola 600 ml" medida en piezas no dice 600
 *   piezas; dice cómo se llama el refresco. Antes se leía como 600 y avisaba.
 * - **Número pelón grande = silencio.** Un "355" suelto es el nombre de una lata, no una
 *   cantidad. Sólo se interpreta lo que de verdad se usa como tamaño (hasta 20).
 */
export function cantidadDeEtiqueta(etiqueta: string, unidad = ''): number | null {
  const texto = etiqueta.trim().toLowerCase();
  const magnitudBase = magnitudDeUnidad(unidad);

  const sufijo = unidad ? SUFIJOS.find((s) => s.patron.test(texto)) : undefined;
  if (sufijo && sufijo.magnitud !== magnitudBase) return null;

  const escala = sufijo?.sub ? 1000 : 1;
  const conSufijo = sufijo !== undefined;

  // "1 1/2 kg"
  const mixta = texto.match(/^(\d+)\s+(\d+)\s*\/\s*(\d+)/);
  if (mixta) {
    const [, entero, num, den] = mixta;
    const d = Number(den);
    if (d === 0) return null;
    return limpiar((Number(entero) + Number(num) / d) / escala);
  }
  // "1/2", "3/4 kg"
  const fraccion = texto.match(/^(\d+)\s*\/\s*(\d+)/);
  if (fraccion) {
    const d = Number(fraccion[2]);
    if (d === 0) return null;
    return limpiar(Number(fraccion[1]) / d / escala);
  }
  const decimal = texto.match(/^(\d+(?:[.,]\d+)?)/);
  if (decimal) {
    const valor = Number(decimal[1].replace(',', '.'));
    // Sin sufijo que lo respalde, un número grande es un nombre comercial, no un tamaño.
    if (!conSufijo && valor > 20) return null;
    return limpiar(valor / escala);
  }

  if (texto.startsWith('entero') || texto.startsWith('completo')) return 1;
  if (texto.startsWith('medio') || texto.startsWith('media')) return 0.5;
  if (texto.startsWith('un cuarto') || texto.startsWith('cuarto')) return 0.25;
  return null;
}

/**
 * Porciones que se proponen para un ingrediente nuevo.
 *
 * Lo que se pesa se ofrece en las cuatro fracciones de siempre; lo que se cuenta, en
 * las que su divisibilidad permita. Son una propuesta editable, no una imposición: el
 * negocio borra las que no vende y agrega las que sí.
 */
export function porcionesSugeridas(unidad: string, fraccionMinima?: number | null): Porcion[] {
  const u = unidad.trim().toLowerCase();

  if (esUnidadContinua(u)) {
    return [1, 0.75, 0.5, 0.25].map((cantidad) => ({
      etiqueta: etiquetaDePorcion(cantidad, unidad),
      cantidad,
      precio: 0,
    }));
  }

  // Indivisible (una lata, un pan): una sola porción, la unidad completa.
  if (!fraccionMinima || fraccionMinima >= 1) {
    return [{ etiqueta: etiquetaDePorcion(1, unidad), cantidad: 1, precio: 0 }];
  }

  // Se parte (el pollo en mitades o cuartos): la pieza y sus partes.
  const partes: number[] = [1];
  for (const c of [0.5, 0.25]) {
    if (c >= fraccionMinima - EPSILON) partes.push(c);
  }
  return partes.map((cantidad) => ({
    etiqueta: etiquetaDePorcion(cantidad, unidad),
    cantidad,
    precio: 0,
  }));
}

export interface ProblemaPorcion {
  indice: number;
  /** Redactado para el operador, no para quien programa. */
  mensaje: string;
  /** `true` = no se puede guardar. `false` = conviene revisarlo. */
  bloquea: boolean;
}

/**
 * Revisa la tabla de porciones antes de crear nada.
 *
 * Bloquea lo que produciría un catálogo incorrecto (cantidad imposible, precio en cero,
 * nombres repetidos) y **avisa** cuando el nombre no concuerda con la cantidad, que es
 * el error que nadie detecta después.
 */
export function revisarPorciones(
  porciones: Porcion[],
  unidad: string,
  fraccionMinima?: number | null,
): ProblemaPorcion[] {
  const problemas: ProblemaPorcion[] = [];
  const vistas = new Map<string, number>();

  porciones.forEach((p, indice) => {
    const etiqueta = p.etiqueta.trim();

    if (etiqueta.length === 0) {
      problemas.push({ indice, mensaje: 'Falta el nombre de la porción.', bloquea: true });
    }

    if (!(p.cantidad > 0)) {
      problemas.push({
        indice,
        mensaje: `Falta cuánto ${unidad} lleva esta porción.`,
        bloquea: true,
      });
    } else if (fraccionMinima && fraccionMinima > 0) {
      const pasos = p.cantidad / fraccionMinima;
      if (Math.abs(pasos - Math.round(pasos)) > 1e-6) {
        /*
          Sprint 127 — El mensaje dice qué hacer, no sólo qué pasó.

          El original —*"0.125 no es una cantidad que este ingrediente permita"*— era un callejón
          sin salida: no decía qué cantidades sí, ni quién lo había decidido (nadie: era un
          default nuestro). Con la fracción deducida de los propios tamaños, este aviso ya casi no
          puede salir en el catálogo; queda para las puertas que agregan un tamaño suelto.
        */
        problemas.push({
          indice,
          mensaje:
            `${decirCantidad(p.cantidad, unidad)} no cuadra: esto se maneja en ` +
            `${describirFraccion(fraccionMinima, unidad)}.`,
          bloquea: true,
        });
      }
    }

    if (!(p.precio > 0)) {
      problemas.push({ indice, mensaje: 'Falta el precio de venta.', bloquea: true });
    }

    const clave = etiqueta.toLowerCase();
    if (clave.length > 0) {
      const previo = vistas.get(clave);
      if (previo !== undefined) {
        problemas.push({
          indice,
          mensaje: `"${etiqueta}" está repetido (fila ${previo + 1}).`,
          bloquea: true,
        });
      } else {
        vistas.set(clave, indice);
      }
    }

    // El caso "3/4 kg (500 gramos)": el nombre dice una cosa y el número otra.
    const declarada = cantidadDeEtiqueta(etiqueta, unidad);
    if (declarada !== null && p.cantidad > 0 && Math.abs(declarada - p.cantidad) > 1e-6) {
      problemas.push({
        indice,
        mensaje:
          `Dice "${etiqueta}" pero lleva ${decirCantidad(p.cantidad, unidad)}. ` +
          `Si el nombre es correcto, debería ser ${decirCantidad(declarada, unidad)}.`,
        bloquea: false,
      });
    }
  });

  return problemas;
}

/** Nombre final del producto: "Picaña 1/2 kg". */
export function nombreDePorcion(nombreInsumo: string, etiqueta: string): string {
  return `${nombreInsumo.trim()} ${etiqueta.trim()}`.trim();
}

function limpiar(n: number): number {
  return Math.round(n * 1e6) / 1e6;
}
